import { z } from 'zod';
import { scopeTypeSchema, type CreateRoleInput, type Scope, type UpdateRoleInput } from '@eombor/contracts';
import { apiClient } from '../../auth/model/auth.store.js';
import type { ApiClient } from '../../../shared/api/client.js';

/**
 * Backend javoblarining ish vaqtidagi sxemasi. Manbalar:
 *   `RoleWithPermissions` — `identity/application/role.service.ts`
 *   `AssignmentView`      — `identity/application/assignment.service.ts`
 *
 * `validFrom` / `validTo` backendda `Date`, lekin JSON orqali **satr** bo'lib
 * keladi (ISO-8601) — shuning uchun bu yerda `z.string()`.
 */
/**
 * Rol nomining ekrandagi ko'rinishi. Backend bazasidagi nom o'zgarmaydi —
 * faqat ko'rsatiladigan matn almashtiriladi; `id` va `code` joyida qoladi,
 * shuning uchun rol tekshiruvi buzilmaydi.
 */
const ROLE_NAME_OVERRIDES: Record<string, string> = {
  'Korxona hisobchisi': 'Material buxgalter',
};

export function roleDisplayName(name: string): string {
  return ROLE_NAME_OVERRIDES[name.trim()] ?? name;
}

const roleNameSchema = z.string().transform(roleDisplayName);

export const roleSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: roleNameSchema,
  description: z.string().nullable(),
  isSystem: z.boolean(),
  permissions: z.array(z.string()),
});

export type RoleDto = z.infer<typeof roleSchema>;

export const rolePageSchema = z.object({
  items: z.array(roleSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
});

export const assignmentSchema = z.object({
  id: z.string(),
  userId: z.string(),
  roleId: z.string(),
  roleCode: z.string(),
  roleName: roleNameSchema,
  permissionCount: z.number(),
  scope: z.object({ type: scopeTypeSchema, id: z.string().nullable() }),
  validFrom: z.string(),
  validTo: z.string().nullable(),
  reason: z.string().nullable(),
});

export type AssignmentDto = z.infer<typeof assignmentSchema>;

/** `POST /users/:userId/assignments` tanasi — `createAssignmentSchema` ning mijoz tomoni. */
export interface CreateAssignmentBody {
  roleId: string;
  scope: Scope;
  reason?: string;
}

/** `PATCH /assignments/:id` tanasi — amalda faqat doira to'g'rilanadi. */
export interface UpdateAssignmentBody {
  scope: Scope;
  reason?: string | null;
}

export interface RbacApi {
  listRoles: (limit?: number) => Promise<z.infer<typeof rolePageSchema>>;
  createRole: (input: CreateRoleInput) => Promise<RoleDto>;
  updateRole: (id: string, input: UpdateRoleInput) => Promise<RoleDto>;
  setRolePermissions: (id: string, permissions: string[]) => Promise<RoleDto>;
  listAssignments: (userId: string) => Promise<AssignmentDto[]>;
  createAssignment: (userId: string, body: CreateAssignmentBody) => Promise<AssignmentDto>;
  updateAssignment: (id: string, body: UpdateAssignmentBody) => Promise<AssignmentDto>;
  removeAssignment: (id: string) => Promise<void>;
}

/**
 * Klient in'ektsiya qilinadi — `auth.api.ts` dagi bilan bir xil qoida:
 * modul hech kimni majburan import qilmaydi, testda esa boshqa klient
 * berish mumkin.
 *
 * Ruxsatlar katalogi bu yerda YO'Q: u `@eombor/contracts` dagi `PERMISSIONS`
 * dan olinadi. Backendda ham `GET /permissions` xuddi shu ro'yxatni qaytaradi —
 * ruxsat kod, rol esa ma'lumot.
 */
export const createRbacApi = (client: ApiClient): RbacApi => ({
  listRoles: (limit = 200) =>
    client.apiFetch(`/roles?page=1&limit=${limit}`, { schema: rolePageSchema }),

  createRole: (input) =>
    client.apiFetch('/roles', { method: 'POST', body: JSON.stringify(input), schema: roleSchema }),

  updateRole: (id, input) =>
    client.apiFetch(`/roles/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
      schema: roleSchema,
    }),

  setRolePermissions: (id, permissions) =>
    client.apiFetch(`/roles/${id}/permissions`, {
      method: 'PUT',
      body: JSON.stringify({ permissions }),
      schema: roleSchema,
    }),

  listAssignments: (userId) =>
    client.apiFetch(`/users/${userId}/assignments`, { schema: z.array(assignmentSchema) }),

  createAssignment: (userId, body) =>
    client.apiFetch(`/users/${userId}/assignments`, {
      method: 'POST',
      body: JSON.stringify(body),
      schema: assignmentSchema,
    }),

  updateAssignment: (id, body) =>
    client.apiFetch(`/assignments/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
      schema: assignmentSchema,
    }),

  // 204 — tanasiz javob, shuning uchun sxema yo'q.
  removeAssignment: (id) => client.apiFetch(`/assignments/${id}`, { method: 'DELETE' }),
});

/** Ilovaning yagona klienti ustidagi tayyor ko'rinish. */
export const rbacApi = createRbacApi(apiClient);
